import Modal from '../ui/Modal';
import { useState } from 'react';
import { INSTRUMENTS, GENRES } from '../constants';
import { sortRolesByLevel, activeMusicians } from '../helpers';
import { updateMusician, deleteMusician, setAdminStatus, setOrganizerPermissions } from '../lib/supabase';
import MusicianCard from '../ui/MusicianCard';
import ProfileEditModal from '../modals/ProfileEditModal';

function MusiciansList({ musicians, currentUser, onRefresh }) {
  const [editTarget,setEditTarget]=useState(null);
  const [confirmDelete,setConfirmDelete]=useState(null);
  const [search,setSearch]=useState("");
  const [cityFilter,setCityFilter]=useState("");
  const [instrumentFilter,setInstrumentFilter]=useState("");
  const [genreFilter,setGenreFilter]=useState("");
  const [inviteCopied,setInviteCopied]=useState(false);
  // Le azioni da amministratore passano dal database e possono essere
  // rifiutate. Senza questo, un rifiuto spariva in silenzio e sembrava
  // soltanto che il pulsante non funzionasse.
  const [errore,setErrore]=useState("");

  const inviteAppText=()=>`Ciao! Ti invito su Jammo, l'app per organizzare serate musicali tra musicisti.\nRegistrati qui: https://jammo.live/#/registrati`;

  const handleInviteCopy=()=>{
    navigator.clipboard.writeText(inviteAppText());
    setInviteCopied(true);
    setTimeout(()=>setInviteCopied(false),2500);
  };
  const handleInviteWhatsApp=()=>{
    window.open(`https://wa.me/?text=${encodeURIComponent(inviteAppText())}`,"_blank");
  };
  const isAdmin=currentUser.is_admin;

  // Chi ha cancellato l'account non compare piu' nell'elenco: la sua riga
  // resta nel database solo per non far sparire i brani che aveva proposto.
  const attivi=activeMusicians(musicians);

  const pendingRequests=attivi.filter(m=>m.requested_organizer&&!m.can_create_private_events&&!m.can_create_public_events);

  const cities=[...new Set(attivi.map(m=>m.city).filter(Boolean))].sort();

  const filtered=attivi.filter(m=>{
    if(search&&!m.name.toLowerCase().includes(search.toLowerCase()))return false;
    if(cityFilter&&m.city!==cityFilter)return false;
    if(instrumentFilter){
      const roles=[...(m.sings?["Voce"]:[]),...(m.instruments||[])];
      if(!roles.includes(instrumentFilter))return false;
    }
    if(genreFilter&&!(m.genres||[]).includes(genreFilter))return false;
    return true;
  });

  return (
    <div className="fade-up">
      <div style={{marginBottom:20,textAlign:"center"}}>
        <div style={{fontSize:26,fontWeight:800,color:"var(--text)",lineHeight:1.1}}>Musicisti</div>
        <div style={{fontSize:16,color:"var(--muted)",marginTop:6}}>{filtered.length} di {attivi.length}</div>
      </div>

      {errore&&(
        <div className="alert alert-error" style={{marginBottom:16,wordBreak:"break-word"}}>
          {errore}
        </div>
      )}

      {/* Banner richieste pendenti — solo admin */}
      {isAdmin && pendingRequests.length > 0 && (
        <div style={{marginBottom:16,padding:"12px 16px",background:"rgba(237,180,29,0.08)",border:"1px solid rgba(237,180,29,0.3)",borderRadius:12,display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:8,height:8,borderRadius:"50%",background:"#e6b800",flexShrink:0}}/>
          <div style={{fontSize:16,color:"#e6b800",fontWeight:600}}>
            {pendingRequests.length === 1
              ? `${pendingRequests[0].name.split(" ")[0]} ha richiesto di organizzare serate`
              : `${pendingRequests.length} musicisti hanno richiesto di organizzare serate`}
            {" — "}
            <span style={{fontWeight:400,color:"rgba(230,184,0,0.7)"}}>apri la card per abilitare</span>
          </div>
        </div>
      )}

      {/* Invita un amico su Jammo */}
      <div style={{marginBottom:20,padding:"14px 16px",background:"var(--surface)",border:"1px solid var(--border)",borderRadius:14,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
        <div style={{minWidth:0}}>
          <div style={{fontSize:17,fontWeight:700,color:"var(--text)"}}>Invita un amico musicista</div>
          <div style={{fontSize:16,color:"var(--muted)",marginTop:2}}>Fai crescere la community su Jammo</div>
        </div>
        <div style={{display:"flex",gap:8,flexShrink:0}}>
          <button className="btn btn-secondary btn-sm" onClick={handleInviteCopy}>
            {inviteCopied?"Copiato!":"Copia"}
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleInviteWhatsApp}>
            WhatsApp
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div style={{marginBottom:12}}>
        <input className="form-input" placeholder="Cerca per nome..." value={search} onChange={e=>setSearch(e.target.value)} style={{marginBottom:8}}/>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {cities.length>1&&<select className="form-select" style={{fontSize:17,padding:"10px 13px"}}
            value={cityFilter} onChange={e=>setCityFilter(e.target.value)}>
            <option value="">Tutte le città</option>
            {cities.map(c=><option key={c}>{c}</option>)}
          </select>}
          <select className="form-select" style={{fontSize:17,padding:"10px 13px"}}
            value={instrumentFilter} onChange={e=>setInstrumentFilter(e.target.value)}>
            <option value="">Tutti gli strumenti</option>
            <option value="Voce">Voce</option>
            {INSTRUMENTS.map(i=><option key={i}>{i}</option>)}
          </select>
          <select className="form-select" style={{fontSize:17,padding:"10px 13px"}}
            value={genreFilter} onChange={e=>setGenreFilter(e.target.value)}>
            <option value="">Tutti i generi</option>
            {GENRES.map(g=><option key={g}>{g}</option>)}
          </select>
          {(search||cityFilter||instrumentFilter||genreFilter)&&
            <button className="btn btn-secondary btn-sm" onClick={()=>{setSearch("");setCityFilter("");setInstrumentFilter("");setGenreFilter("");}}>Reset filtri</button>}
        </div>
      </div>

      {filtered.length===0&&<div className="empty-state">
        <div style={{marginBottom:12}}>Nessun musicista con questi filtri.</div>
        {(search||cityFilter||instrumentFilter||genreFilter)&&
          <button className="btn btn-secondary btn-sm" onClick={()=>{setSearch("");setCityFilter("");setInstrumentFilter("");setGenreFilter("");}}>Rimuovi filtri</button>
        }
      </div>}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
        {filtered.map(m=>{
          const levels=m.levels||{};
          const allRoles=sortRolesByLevel(m.instruments||[], m.sings, levels);
          return <MusicianCard key={m.id} m={m} allRoles={allRoles} levels={levels} isAdmin={isAdmin}
            onEdit={()=>setEditTarget(m)}
            onDelete={()=>setConfirmDelete(m)}
            onToggleAdmin={async()=>{
              setErrore("");
              try{ await setAdminStatus(m.id,!m.is_admin); onRefresh(); }
              catch(e){ console.error(e); setErrore(e?.message||"Operazione rifiutata dal database."); }
            }}
            onTogglePermission={async(canPrivate,canPublic)=>{
              setErrore("");
              try{ await setOrganizerPermissions(m.id,canPrivate,canPublic); onRefresh(); }
              catch(e){ console.error(e); setErrore(e?.message||"Operazione rifiutata dal database."); }
            }}/>;
        })}
      </div>

      {editTarget&&<ProfileEditModal musician={editTarget} title={`Modifica: ${editTarget.name}`} onClose={()=>setEditTarget(null)}
        onSave={async(data)=>{await updateMusician(editTarget.id,data);onRefresh();setEditTarget(null);}}/>}

      {confirmDelete&&<Modal onClose={()=>setConfirmDelete(null)}>
          <div className="modal-title">Elimina membro</div>
          <div style={{marginBottom:16,fontSize:17,color:"var(--muted)"}}>Eliminare <strong style={{color:"var(--text)"}}>{confirmDelete.name}</strong>?</div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={()=>setConfirmDelete(null)}>Annulla</button>
            <button className="btn btn-danger" onClick={async()=>{await deleteMusician(confirmDelete.id);setConfirmDelete(null);onRefresh();}}>Sì, elimina</button>
          </div>
      </Modal>}
    </div>
  );
}

export default MusiciansList;
